import React, { useEffect, useState } from "react";
import { 
  Box, 
  VStack, 
  Text, 
  Collapse,
  Progress,
  Button,
  HStack,
  Link,
  Icon
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { useChat } from "context/ChatBotContext";
import { AnalysisTreeMap } from "containers/Interfaces";
import { useAnalysisDrawerContext } from "context/AnalysisDrawerContext";
import { ToolCall as ToolCallType, ToolCall_ToolType, toolCall_ToolTypeToJSON } from "build-ts/chat";
import { FaCircleCheck } from "react-icons/fa6";

interface ToolCallProps {
  showProgress: boolean;
  toolCalls: ToolCallType[];
}

const ToolCall: React.FC<ToolCallProps> = ({ 
  showProgress,
  toolCalls 
}) => {
  const { isLoading, setReferencedNodeId } = useChat();
  const [isOpen, setIsOpen] = useState(false);
  const { responseData, setSelectedUuid } = useAnalysisDrawerContext();

  useEffect(() => {
    if (isLoading && showProgress) {
      setIsOpen(true);
    }
  }, [isLoading, showProgress]);

  // Helper function to find tree UUID in response data
  const handleTreeIdClick = (responseData: { [key: string]: AnalysisTreeMap }, id: string): string | null => {
    for (const key in responseData) {
      if (responseData.hasOwnProperty(key)) {
        const analysisTreeMap = responseData[key];
        if (analysisTreeMap.tree?.header?.id === id) {
          return key;
        }
      }
    }
    return null;
  };
  
  const TreeIdLink: React.FC<{ treeId: string }> = ({ treeId }) => {
    const uuid = handleTreeIdClick(responseData, treeId);
    const displayName = uuid ? responseData[uuid]?.tree?.header?.name || treeId : treeId;
  
    return (
      <Link 
        color="teal.500" 
        onClick={() => uuid && setSelectedUuid(uuid)}
        style={{ cursor: "pointer" }}
        fontWeight={600}
      >
        {displayName}
      </Link>
    );
  };

  const getToolFriendlyName = (toolName: string | ToolCall_ToolType | undefined): string => {
    
    switch (toolName) {
      case "GET_TREE_SCHEMA":
        return "Studying schema for";
      case "GET_NODES_DATA":
        return "Analyzing data from";
      case "GET_TREE_SUMMARY":
        return "Summarizing";
      case "TIGA_AS_A_TOOL":
        return "TIGA as a tool";
      case "UPDATE_CUSTOM_INSTRUCTIONS":
        return "Updating custom instructions";
      case "INVALID_TOOL":
        return "Invalid tool";
      case "UNRECOGNIZED":
        return "Unrecognized Tool";
      default:
        return "Unrecognized Tool";
    }
  };

  if (!toolCalls?.length && !showProgress) {
    return null;
  }

  return (
    <Box w="full" mb={0}>
      <Button
        size="xs"
        variant="ghost"
        onClick={() => setIsOpen(!isOpen)}
        w="full"
        mt={0}
        mb={0}
        rightIcon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
        color="gray.600"
        _hover={{bg: "gray.200"}}
        bg="gray.100"
        borderRadius={isOpen ? "xs" : "lg"}
      >
        <HStack justify="space-between" w="full">
          <Text fontSize="xs" mt={0} mb={0} color="gray.500" fontWeight={500}>
            {(isLoading && showProgress) ? "Working..." : `Used ${toolCalls.length} tool${toolCalls.length !== 1 ? "s" : ""}`}
          </Text>
        </HStack>
      </Button>

      <Collapse in={isOpen}>
        <Box 
          w="full" 
          bg="gray.100" 
          p={1}
        >
          <VStack w="full" spacing={1} p={1}>
            {toolCalls.map((tool, index) => (
              <Box key={tool.call_id || index} w="full">
                <HStack w="full" justify="space-between" align="center">
                  <Text fontSize="xs" color="gray.600" mb={0} textAlign="left">
                    {typeof(tool.tool_name) === "string" ? getToolFriendlyName(tool.tool_name) : getToolFriendlyName(toolCall_ToolTypeToJSON(tool.tool_name!))}
                    {tool.tool_args && (
                      <>
                        {tool.tool_args?.node_ids && tool.tool_args?.node_ids?.length! > 0 && (
                          <>
                            {" for "}
                            {tool.tool_args?.node_ids.map((node_id, idx) => (
                              <React.Fragment key={node_id}>
                                <Link 
                                  color="teal.500" 
                                  onClick={() => {
                                    // First find the tree that contains this node
                                    if (tool.tool_args?.tree_id) {
                                      // Find the UUID for this tree
                                      const uuid = handleTreeIdClick(responseData, tool.tool_args.tree_id);
                                      if (uuid) {
                                        // Set the selected UUID to navigate to the tree
                                        setSelectedUuid(uuid);
                                        setReferencedNodeId({ nodeId: node_id, treeId: tool.tool_args.tree_id });
                                      }
                                    }
                                  }}
                                  style={{ cursor: "pointer" }}
                                >
                                  {node_id.substring(0, 3)}...
                                </Link>
                                {idx < tool.tool_args?.node_ids?.length! - 1 ? ", " : ""}
                              </React.Fragment>
                            ))}
                          </>
                        )}
                        {tool.tool_args?.tree_ids && tool.tool_args?.tree_ids?.length > 0 && (
                          <>
                            {": "}
                            {tool.tool_args?.tree_ids.map((tree_id, idx) => (
                              <React.Fragment key={tree_id}>
                                <TreeIdLink treeId={tree_id} />
                                {idx < tool.tool_args?.tree_ids?.length! - 1 ? ", " : ""}
                              </React.Fragment>
                            ))}
                          </>
                        )}
                        {tool.tool_args?.tree_id && (
                          <>
                            {" in "}
                            <TreeIdLink treeId={tool.tool_args?.tree_id!} />
                          </>
                        )}
                      </>
                    )}
                  </Text>
                  {(!isLoading || index < toolCalls.length - 1) && (
                    <Icon as={FaCircleCheck} color="green.500" flexShrink={0} boxSize={4} mt={0} />
                  )}
                </HStack>

                {showProgress && isLoading && index === toolCalls.length - 1 && (
                  <Progress 
                    size="xs" 
                    isIndeterminate 
                    colorScheme="purple"
                    bg="gray.300"
                  />
                )}
              </Box>
            ))}
          </VStack>
        </Box>
      </Collapse>
    </Box>
  );
};

export default ToolCall;  